<template>
  <div>
    <div class="px-4 md:px-6 flex flex-col bg-form-grey-lighter pb-4 pt-7">

      <!-- title -->
      <h2 class="slate-600 font-bold text-lg">
        {{ `${$t('messages_nl.cancellation.view.steps.form.2')}` }}
      </h2>

      <!-- payment options -->
      <adivare-accordion
        v-if="isSetupComplete"
        :items="getPayments"
        :speed="1"
        :expand-checked-radio-button="true"
        :disable-multi-toggle="false"
        show-divider
        class="mb-1 mt-4"
      >
        <template #button="item">
          <div class="flex items-center cursor-pointer">

            <!-- payment options: checkbox  -->
            <input
              v-model="selectedPaymentId"
              :value="item.item.id"
              :id="`product_${item.item.id}`"
              type="radio"
              name="payment_type"
              class="form--choicetype-radio__radio-dark mt-0 py-1.5"
            >
            <label
              :for="`product_${item.item.id}`"
              class="flex items-center justify-between flex-1 cursor-pointer py-1.5 text-left"
            >
              <i18n-t
                :keypath="`messages_nl.product.option.${item.item.id}.button`"
                tag="p"
                scope="global"
              >
                <template v-slot:free>
                  <strong>{{ $t('messages_nl.product.free') }}</strong>
                </template>
              </i18n-t>
              <div
                v-if="3 === item.item.id"
                class="hidden lg:flex bg-dark-blue py-1 px-1.5 mx-1 pointer-events-none rounded items-center text-white font-bold text-xs"
              >
                <img
                  :src="$asset('img/cancellation/mostPopular.svg')"
                  :alt="$t('messages_nl.product.mostPopular')"
                  loading="lazy"
                  class="mr-2"
                  width="17"
                  height="17"
                >
                {{ $t('messages_nl.product.mostPopular') }}
              </div>
              <span class="font-bold text-slate-600 whitespace-nowrap">{{ item.item.price }}</span>
            </label>
          </div>
          <div
            v-if="item.item.id === 3"
            class="flex justify-center lg:hidden my-4 bg-dark-blue py-1 px-2 rounded items-center text-white font-bold text-xs relative after:content-[''] after:inline-block after:absolute after:mx-auto after:w-4 after:h-4 after:bg-dark-blue after:rotate-45 after:left-0 after:right-0 after:-top-2"
          >
            <img
              :src="$asset('img/cancellation/mostPopular.svg')"
              :alt="$t('messages_nl.product.mostPopular')"
              loading="lazy"
              class="mr-2"
              width="17"
              height="17"
            >
            {{ $t('messages_nl.product.mostPopular') }}
          </div>
        </template>
        <template #content="item">
          <div class="block pt-1 pb-4">
            {{ $t(`messages_nl.product.option.${item.item.id}.info`) }}
          </div>
        </template>
      </adivare-accordion>
    </div>

    <!-- divider image -->
    <img
      :src="$asset('img/common/triangles/triangle-down-form-2.svg')"
      alt=""
      loading="lazy"
      class="w-full mb-2 h-8 bg-white"
    />

    <div class="pt-1 pb-6 px-4 md:px-6 flex flex-col bg-white">

      <!-- optional product -->
      <div
        v-if="getOptionalProduct"
        class="checkbox mt-4"
      >
        <div
          data-toggle="buttons"
          class="flex flex-nowrap"
        >
          <input
            type="checkbox"
            class="form--choicetype-checkbox__checkbox !mt-0.5"
            id="order_form_userLetterType_optionalProduct"
            name="order_form[userLetterType][optionalProduct]"
            v-model="selectedOptionalProduct"
          >
          <label
            for="order_form_userLetterType_optionalProduct"
            class="text-fontcolor-light-grey !inline-block md:flex-1 md:!flex md:flex-row md:justify-between"
          >
            {{ $t(`messages_nl.product.option.${ getOptionalProduct.id }.button`) }}
            <span class="font-bold text-blue whitespace-nowrap">+ {{ getOptionalProduct.price }}</span>
          </label>
        </div>
      </div>

      <!-- input field: IBAN -->
      <component
        v-if="0 === getSelectedPaymentPrice"
        :is="getIbanParameters.component"
        :property-name="getIbanParameters.propertyName"
        :input-class="`${getIbanParameters.inputClass} w-full`"
        :legend-class="getIbanParameters.legendClass"
        :outer-class="`${getIbanParameters.outerClass} mt-3 mb-2`"
        :label-key="getIbanParameters.translationKey"
        :label-text="getIbanParameters.name"
        :form-type="getIbanParameters.formType"
        :minlength="getIbanParameters.minlength"
        :maxlength="getIbanParameters.maxlength"
        :optional="getIbanParameters.optional"
      />

      <!-- checkbox: terms and condition -->
      <div class="checkbox mt-4 mb-2">
        <div
          data-toggle="buttons"
          class="flex flex-nowrap"
        >
          <input
            type="checkbox"
            class="form--choicetype-checkbox__checkbox !mt-0.5"
            id="order_form_paymentType_agreeTerms"
            name="order_form[paymentType][agreeTerms]"
            required="required"
            v-model="agreeTerms"
            aria-describedby="agreeTerms-error"
            :aria-invalid="showErrors && !agreeTerms ? 'true' : 'false'"
          >
          <label
            for="order_form_paymentType_agreeTerms"
            class="text-fontcolor-light-grey flex flex-col md:flex-row"
          >
            <i18n-t
              keypath="messages_nl.label.cancellation.terms.text"
              tag="p"
              class="disable-preflight"
              scope="global"
            >
              <template v-slot:terms>
                <a
                  :href="$path('terms-and-conditions')"
                  target="_blank"
                >
                  {{ $t('messages_nl.label.cancellation.terms.link') }}
                </a>
              </template>
            </i18n-t>
          </label>
        </div>
        <div id="agreeTerms-error">
          <adivare-alert
            v-if="showErrors && !agreeTerms"
            class="mt-3"
            :messages="$t('messages_nl.label.cancellation.terms.error')"
            size="xs"
            show-icon
          />
        </div>
      </div>

      <!-- text: terms and condition -->
      <p
        v-if="priceAsFloat > 0"
        class="block mt-2"
      >
        {{ $t('messages_nl.label.cancellation.payment.explain', { price }) }}
      </p>

      <vue-turnstile
        class="mt-5"
        :site-key="this.cloudflareTurnstileSiteKey"
        action="cancellation-form"
        theme="light"
        appearance="interaction-only"
        size="normal"
        language="auto"
        render-on-mount=true
        @error="turnstileErrorCallback"
        @unsupported="turnstileUnsupportedCallback"
        @expired="turnstileExpiredCallback"
        @after-interactive="turnstileAfterInteractiveCallback"
        v-model="token"
      />

      <div id="security-error">
        <adivare-alert
          v-if="showErrors && !token"
          type="info"
          class="mt-3"
          :messages="$t('messages_nl.label.cancellation.security.notSet')"
          size="xs"
          show-icon
        />
        <adivare-alert
          v-if="showTurnstileUnsupportedAlert"
          class="mt-3"
          :messages="$t('messages_nl.label.cancellation.security.unsupported')"
          size="xs"
          show-icon
        />
        <adivare-alert
          v-if="showTurnstileErrorAlert && !token"
          class="mt-3"
          :messages="$t('messages_nl.label.cancellation.security.error')"
          size="xs"
          show-icon
        />
      </div>

      <!-- submit button -->
      <button
        type="button"
        class="click-theme click-theme--primary click-theme--size-md my-4"
        @click="submitCancellation"
      >
        <span
          v-if="hasSubmitted"
          class="loader-bars loader-bars--size-md click-theme__loader"
        ></span>
        <span :style="{visibility: !hasSubmitted ? 'visible' : 'hidden'}">
          {{ $t('messages_nl.product.option.5.button') }}
        </span>
      </button>
      <div class="mb-2 text-fontcolor-light-grey text-sm">
        <!-- terms info -->
        <i18n-t
          keypath="messages_nl.partials.letter.rules.text"
          tag="p"
          class="disable-preflight"
          scope="global"
        >
          <template v-slot:privacy>
            <a
              :href="$path('privacy.declaration')"
              target="_blank"
            >
              {{ $t('messages_nl.partials.letter.rules._privacyLink') }}
            </a>
          </template>
          <template v-slot:terms>
            <a
              :href="$path('terms-and-conditions')"
              target="_blank"
            >
              {{ $t('messages_nl.partials.letter.rules._termsLink') }}
            </a>
          </template>
        </i18n-t>
      </div>
    </div>
  </div>
</template>

<script>
import AdivareFormElement from "./partials/default-form-element";
import { AdivareSpinner } from '@adivare/vue-components';
import AdivareAccordion from '../../../components/accordion';
import AdivareIbanFormElement from "./partials/iban-form-element";
import AdivareAlert from '../../../components/alert';
import client from "../../../axios-client";
import submitMixin from "../../mixins/submit-mixin";
import { mapGetters } from 'vuex';
import VueTurnstile from 'vue-turnstile';

export default {
  name: 'adivare-letter-price',
  components: {
    AdivareFormElement,
    AdivareIbanFormElement,
    AdivareSpinner,
    AdivareAlert,
    AdivareAccordion,
    VueTurnstile,
  },
  props: {
    cloudflareTurnstileSiteKey: {
      required: true,
      type: String,
    },
  },
  mixins: [
    submitMixin,
  ],
  data() {
    return {
      paymentTypeSet: false,
      hasSubmitted: false,
      showTurnstileUnsupportedAlert: false,
      showTurnstileErrorAlert: false,
      token: null,
    };
  },
  computed: {
    ...mapGetters([
      'getIbanParameters',
      'getPayments',
      'getErrorCount',
      'getFormField',
      'getPaymentTypeField',
      'getOptionalProduct',
      'getSelectedPaymentPrice',
      'hasValidSignature',
      'isSetupComplete',
      'totalPrice',
      'showErrors',
    ]),
    selectedPaymentId: {
      get() {
        this.updatePrice();

        return this.getFormField('product');
      },
      set(value) {
        this.$store.dispatch('updateFormField', {
          name: 'product',
          value: value,
        });
        this.$store.commit('updatePaymentTypeField', {
          name: 'paymentType',
          value: value,
        });
        this.updatePrice();
      }
    },
    selectedOptionalProduct: {
      get() {
        return this.getFormField('optionalProduct');
      },
      set(value) {
        this.$store.dispatch('updateFormField', {
          name: 'optionalProduct',
          value: value,
        }).then(this.updatePrice);
      }
    },
    agreeTerms: {
      get() {
        return this.getPaymentTypeField('agreeTerms');
      },
      set(value) {
        this.$store.commit('updatePaymentTypeField', {
          name: 'agreeTerms',
          value: value,
        })
      }
    },
    price() {
      return this.totalPrice?.toFixed(2).replace('.', ',');
    },
    priceAsFloat() {
      if (!this.price) {
        return 0.0;
      }
      return parseFloat(this.price.replace(',', '.'));
    },
  },
  mounted() {
    this.$store.commit('addFormField', {
      name: 'optionalProduct',
      value: false,
      optional: true,
    });
    this.updatePrice();
  },
  methods: {
    turnstileUnsupportedCallback() {
      this.showTurnstileUnsupportedAlert = true;
    },
    turnstileExpiredCallback() {
      this.showTurnstileErrorAlert = true;
    },
    turnstileErrorCallback() {
      this.showTurnstileErrorAlert = true;
    },
    turnstileAfterInteractiveCallback() {
      this.showTurnstileErrorAlert = false;
    },
    submitCancellation() {
      this.setPaymentType();
      if (
        0 < this.getErrorCount
        || false === this.hasValidSignature
        || false === this.agreeTerms
        || !this.token
      ) {
        this.$store.commit('showErrors', true);

        return;
      }

      this.$store.dispatch('submit').then(isValid => {
        console.debug(`Submit promise resolved to: ${isValid}`);
        if (true === isValid && false === this.hasSubmitted) {
          this.hasSubmitted = true;
          const userLetterType = {
            ...this.$store.getters.getFormFields,
            [this.$store.getters.getIbanParameters.propertyName]: this.$store.getters.getIbanFormField,
            [this.$store.getters.getReasonToCancelParameters.propertyName]: this.$store.getters.getReasonToCancelFormField,
            additionalFields: this.$store.getters.getAdditionalFormFieldValues,
          };
          userLetterType.optionalProduct = userLetterType.optionalProduct ? this.getOptionalProduct.id : null;
          client
            .postForm(this.$store.getters.cancellationId, {
              userLetterType,
              paymentType: this.$store.getters.getPaymentType,
              security: this.token,
            })
            .then(response => window.location.href = response.data.data.url)
            .catch(error => this.handleSubmitError(error));
        } else {
          this.dispatch('scrollToTop');
        }
      });
    },
    updatePrice() {
      this.$store.dispatch('updateTotalPrice')
    },
    /**
     * Paymenttype 1 means billing, Paymenttype 2 means free.
     */
    setPaymentType() {
      this.$store.commit('updatePaymentTypeField', {
        name: 'paymentType',
        value: 1 + (this.priceAsFloat <= 0),
      });
      this.paymentTypeSet = true;
    },
  },
}
</script>
